import { OktaAuth as OktaClient } from '@okta/okta-auth-js';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useRef } from 'react';
import { NotAuthenticatedError } from '@/errors';
import { GetCurrentUser, GetCurrentUserContext } from '@/generated/graphql';
import { gql, useQuery as useGqlQuery } from '@/graphql';
import {
  AuthenticationConfig,
  AuthenticationContext,
  AuthenticationContextValue,
  CLI_SCOPES,
  SCOPES,
  User,
} from './AuthenticationContext';

type LocationDescriptor = { pathname: string; search: string };

function getRedirectUri(pkceEnabled: boolean, authRoutes: AuthRoutes) {
  return pkceEnabled
    ? `${window.location.origin}${authRoutes.LOGIN_PKCE_CALLBACK}`
    : `${window.location.origin}${authRoutes.LOGIN_CALLBACK}`;
}

function getRedirectPath(authRoutes: AuthRoutes) {
  return `${authRoutes.LOGIN_CALLBACK}`;
}

function getRedirectPkcePath(authRoutes: AuthRoutes) {
  return `${authRoutes.LOGIN_PKCE_CALLBACK}`;
}

function getImpersonateRedirectUri(pkceEnabled: boolean, authRoutes: AuthRoutes) {
  return pkceEnabled
    ? `${window.location.origin}${authRoutes.IMPERSONATE_PKCE_CALLBACK}`
    : `${window.location.origin}${authRoutes.IMPERSONATE_CALLBACK}`;
}

export const POST_LOGIN_REDIRECT_URL_PARAM = 'redirectTo';

export function getLoginPageLocation(loginRoute: string): LocationDescriptor {
  const current = globalThis.location;

  // We only need the relative URL for redirecting after login
  const redirectTo = current.pathname + current.search + current.hash;

  const params = new URLSearchParams();
  params.set(POST_LOGIN_REDIRECT_URL_PARAM, redirectTo);

  return {
    pathname: loginRoute,
    search: `?${params.toString()}`,
  };
}

/**
 * GraphQL query used to load the current user.
 */
export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      email
      isAdmin
    }
  }
`;

interface ConfigState {
  config?: AuthenticationConfig;
  oktaClient?: OktaClient;
  oktaCliClient?: OktaClient;
}

export interface AuthenticationContextProviderProps {
  children?: React.ReactNode;
  fetchFn: typeof globalThis.fetch;
  loginRoute: string;
  authRoutes: AuthRoutes;
}

// Routes needed by the provider to redirect to the appropriate places
export interface AuthRoutes {
  LOGIN_CALLBACK: string;
  LOGIN_PKCE_CALLBACK: string;
  IMPERSONATE_CALLBACK: string;
  IMPERSONATE_PKCE_CALLBACK: string;
  CLI_LOGIN_CALLBACK: string;
  CLI_IMPERSONATE_CALLBACK: string;
  LOGOUT_CALLBACK: string;
}

// Number of times to retry loading auth configuration before erroring
const CONFIG_RETRY_ATTEMPTS = 3;

/**
 * Provides app-level authentication information via context.
 */
export function AuthenticationContextProvider(props: AuthenticationContextProviderProps) {
  const { children, fetchFn, loginRoute, authRoutes } = props;

  const configRequest = useQuery({
    queryKey: ['authConfigQuery'],
    queryFn: async () => {
      const response = await fetchFn('/auth/config');
      return (await response.json()) as AuthenticationConfig;
    },
    retry: CONFIG_RETRY_ATTEMPTS,
  });

  const impersonateConfigRequest = useQuery({
    queryKey: ['authImpersonateConfigQuery'],
    queryFn: async () => {
      const response = await fetchFn('/auth/impersonate/config');
      return (await response.json()) as AuthenticationConfig;
    },
    retry: CONFIG_RETRY_ATTEMPTS,
  });

  // Recalculate configState whenever the config from the server changes
  const configState = useRef<ConfigState>({});
  const impersonateConfigState = useRef<ConfigState>({});

  if (configState.current.config !== configRequest.data) {
    const config = configRequest.data;
    // Otherwise, create appropriate state
    let oktaClient: OktaClient | undefined = undefined;
    let oktaCliClient: OktaClient | undefined = undefined;
    if (config?.type === 'okta') {
      oktaClient = new OktaClient({
        clientId: config.okta.client_id,
        issuer: config.okta.issuer,
        redirectUri: getRedirectUri(config?.enable_pkce_auth_flow, authRoutes),
        scopes: SCOPES,
        postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
        pkce: config?.enable_pkce_auth_flow,
        tokenManager: {
          // TODO: Can we use this rather than hitting our server?
          autoRenew: false,
        },
      });
      oktaCliClient = new OktaClient({
        clientId: config.okta.client_id,
        issuer: config.okta.issuer,
        redirectUri: `${window.location.origin}${authRoutes.CLI_LOGIN_CALLBACK}`,
        scopes: CLI_SCOPES,
        postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
        pkce: false,
        tokenManager: {
          // TODO: Can we use this rather than hitting our server?
          autoRenew: false,
        },
      });
    }

    configState.current = {
      config,
      oktaClient,
      oktaCliClient,
    };
  }

  if (!impersonateConfigRequest.error && impersonateConfigState.current.config !== impersonateConfigRequest.data) {
    const config = impersonateConfigRequest.data;

    // Otherwise, create appropriate state
    let oktaClient: OktaClient | undefined = undefined;
    let oktaCliClient: OktaClient | undefined = undefined;
    if (config?.type === 'okta') {
      oktaClient = new OktaClient({
        clientId: config.okta.client_id,
        issuer: config.okta.issuer,
        redirectUri: getImpersonateRedirectUri(config?.enable_pkce_auth_flow, authRoutes),
        // TODO: Redirect to an admin specific logout callback
        postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
        // TODO: This will force the older 'implicit' flow, we should figure out how to migrate to PKCE
        pkce: config?.enable_pkce_auth_flow,
        tokenManager: {
          // TODO: Can we use this rather than hitting our server?
          autoRenew: false,
        },
      });
      oktaCliClient = new OktaClient({
        clientId: config.okta.client_id,
        issuer: config.okta.issuer,
        redirectUri: `${window.location.origin}${authRoutes.CLI_IMPERSONATE_CALLBACK}`,
        // TODO: Redirect to an admin specific logout callback
        postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
        pkce: false,
        tokenManager: {
          // TODO: Can we use this rather than hitting our server?
          autoRenew: false,
        },
      });
    }

    impersonateConfigState.current = {
      config,
      oktaClient,
      oktaCliClient,
    };
  }

  // Load the currently logged in user from the GraphQL API
  const [{ fetching: loading, error, data }, refreshCurrentUser] = useGqlQuery<GetCurrentUser>({
    query: GET_CURRENT_USER,
    context: GetCurrentUserContext,
    requestPolicy: 'network-only',
  });

  let currentUser: User | undefined = undefined;

  // NotAuthenticatedErrors are fine and can be ignored since we might not be logged in yet,
  // otherwise we want to throw
  if (error !== undefined && error.networkError instanceof NotAuthenticatedError === false) {
    throw error;
  }

  currentUser = data?.me;

  const refreshAuthToken = useCallback(async () => {
    const pkceEnabled =
      configState.current.config?.enable_pkce_auth_flow || impersonateConfigState.current.config?.enable_pkce_auth_flow;
    const redirectPath = pkceEnabled ? getRedirectPkcePath(authRoutes) : getRedirectPath(authRoutes);

    const response = await fetchFn('/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ redirect_path: redirectPath }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh auth token');
    }
  }, [authRoutes, fetchFn]);

  const authContext: AuthenticationContextValue = useMemo(
    () => ({
      loginRoute,
      isLoading: loading || configRequest.isFetching || impersonateConfigRequest.isFetching,
      config: configState.current.config,
      oktaClient: configState.current.oktaClient,
      oktaImpersonateClient: impersonateConfigState.current.oktaClient,
      oktaCliClient: configState.current.oktaCliClient,
      oktaCliImpersonateClient: impersonateConfigState.current.oktaCliClient,
      oktaImpersonateConfig: impersonateConfigState.current.config,
      pkceEnabled:
        impersonateConfigState.current.config?.enable_pkce_auth_flow ||
        configState.current.config?.enable_pkce_auth_flow,
      currentUser,
      refreshCurrentUser,
      refreshAuthToken,
    }),
    [loading, configRequest, impersonateConfigRequest, currentUser, refreshCurrentUser, loginRoute, refreshAuthToken]
  );

  return <AuthenticationContext.Provider value={authContext}>{children}</AuthenticationContext.Provider>;
}
